import React, { useContext } from "react";
import { Modal, Figure } from "react-bootstrap";
import API, { API_URL } from "../services/api";
import { GroupContext } from "../services/contexts/GroupContext";
import { forgetPassword } from "../services/auth";
import Confirmation from "./Confirmation";
import CustomButton from "./common/CustomButton";
import classes from "./EditUserModal.module.css";
import { contactSupportText } from "./common/utils/constants";
import MultiSelect from "./common/MultiSelect";
import toast from "react-hot-toast";
const ALLOWED_MIME_TYPES = [
  "image/png",
  "image/jpg",
  "image/jpeg",
  "image/gif",
];

function EditUserModal({ Children, userData, refetch, currentUserId }) {
  const { globalData, getLatestData } = useContext(GroupContext);
  const [userState, setUserState] = React.useState(userData);
  const [isOpen, setIsOpen] = React.useState(false);
  const [avatar, setAvatar] = React.useState({
    img: userData.avatar
      ? `${API_URL}/getAvatar?member_avatar=${userData.avatar}`
      : "",
    obj: null,
  });
  const fileInputRef = React.useRef(null);

  const [isUpdatingEmail, setIsUpdatingEmail] = React.useState(false);

  const [confirmEmail, setConfirmEmail] = React.useState(null);

  const handleOpen = () => {
    setUserState(userData);
    setAvatar({
      img: userData.avatar
        ? `${API_URL}/getAvatar?member_avatar=${userData.avatar}`
        : "",
      obj: null,
    });
    setIsOpen(true);
  };

  const handleClose = () => {
    setAvatar({
      img: userData.avatar
        ? `${API_URL}/getAvatar?member_avatar=${userData.avatar}`
        : "",
      obj: null,
    });
    setUserState(userData);
    setIsUpdatingEmail(false);
    setConfirmEmail(null);
    setIsOpen(false);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const noFileUploaded = file === undefined || file === null;
    const fileTypeIsAllow =
      !noFileUploaded && ALLOWED_MIME_TYPES.includes(file.type);
    if (noFileUploaded) {
      setAvatar({ img: "", obj: null });
    } else if (fileTypeIsAllow) {
      const url = URL.createObjectURL(e.target.files[0]);
      setAvatar({
        ...avatar,
        img: url,
        obj: e.target.files[0],
      });
    } else {
      toast.error(`File type: ${file.name.split(".").pop()} is not allow`);
      setAvatar({ img: "", obj: null });
      fileInputRef.current.value = null;
    }
  };

  const { mutateAsync: sendResetLinkMutation } = forgetPassword({
    onSuccess: (response) => {
      alert(response.msg);
    },
    onError: () => {
      alert(
        "Something goes wroing, please contact It support at itsupport@caipa.com"
      );
    },
  });

  async function handleUpdateUser() {
    try {
      if (avatar.img !== "" && avatar.obj !== null) {
        const formData = new FormData();
        formData.append("file", avatar.obj);
        formData.append("user_id", userState.id);
        try {
          await API.put("admin/avatar", formData);
          if (currentUserId === userState.id) {
            // if current admin updates his/her own avatar
            await refetch();
          }
        } catch (err) {
          alert(`Failed to upload avatar, ${contactSupportText}`);
        }
      }
      await API.patch("admin/updateUser", {
        user_id: userState.id,
        first_name: userState.first_name,
        last_name: userState.last_name,
        username: userState.username,
        inactive: userState.inactive,
        allowed_groups: userState.groups.map((group) => group.group_id),
      });

      await getLatestData();

      handleClose();
    } catch (err) {
      if (err) {
        alert(err);
      } else {
        alert(`An error occoured while updating user, ${contactSupportText}`);
      }
    }
  }
  const isEmailInvalid = isUpdatingEmail && userState.username !== confirmEmail;
  const isFormDirty = () => {
    return (
      (avatar && avatar.obj !== null) ||
      userState.first_name !== userData.first_name ||
      userState.last_name !== userData.last_name ||
      userState.username !== userData.username ||
      userState.groups.length !== userData.groups.length ||
      userState.groups.some(
        (item, index) => item.group_id !== userData.groups[index].group_id
      )
    );
  };
  return (
    <>
      <div onClick={handleOpen}>{Children}</div>
      <Modal
        show={isOpen}
        onHide={handleClose}
        dialogClassName={classes.edit_modal_dialog}
        backdropClassName={classes.edit_modal_backdrop}
        contentClassName={classes.edit_modal_content}
        backdrop="static"
      >
        <Modal.Header className="_g-modal-header_">
          <div className="_g-close-button-anchor_">
            <div className="_g-close-button_" onClick={handleClose}>
              &#x2715;
            </div>
          </div>
          Edit User
        </Modal.Header>
        <Modal.Body
          className={`_g-modal-body_ ${classes.edit_modal_body}`}
          id="custom-scroll-y"
        >
          <div>
            <span>Choose a new avatar</span>
            <input
              type="file"
              accept=".png, .jpg, .jpeg, .gif"
              onChange={handleFileChange}
              ref={fileInputRef}
            />
          </div>
          <div className={classes.avatar_wrapper}>
            {avatar && (
              <Figure.Image
                width={200}
                height={200}
                style={{ margin: "auto", objectFit: "contain" }}
                alt="Choose a new avatar"
                src={avatar.img}
                onError={() => {
                  setAvatar({ img: "", obj: null });
                }}
              />
            )}
          </div>
          <div>
            <div>First Name</div>
            <input
              className="form-control"
              type="text"
              value={userState.first_name}
              maxLength={45}
              onChange={(e) => {
                setUserState((prev) => ({
                  ...prev,
                  first_name: e.target.value,
                }));

                if (e.target.value.length === 45) {
                  toast.error(
                    `First name can not be longer than 45 characters.`
                  );
                }
              }}
            />
          </div>
          <div>
            <div>Last Name</div>
            <input
              type="text"
              className="form-control"
              value={userState.last_name}
              maxLength={45}
              onChange={(e) => {
                setUserState((prev) => ({
                  ...prev,
                  last_name: e.target.value,
                }));
                if (e.target.value.length === 45) {
                  toast.error(
                    `Last name can not be longer than 45 characters.`
                  );
                }
              }}
            />
          </div>
          <div>
            <div>Username / Email</div>
            <input
              // readOnly
              type="email"
              className="form-control"
              value={userState.username}
              maxLength={45}
              onChange={(e) => {
                setIsUpdatingEmail(true);
                setUserState((prev) => ({
                  ...prev,
                  username: e.target.value,
                }));
                if (e.target.value.length === 45) {
                  toast.error(
                    `Email can not be longer than 45 characters.`
                  );
                }
              }}
            />
          </div>
          {isUpdatingEmail && (
            <>
              <div>
                <div>Confirm Username / Email Address</div>
                <input
                  className="form-control"
                  type={"email"}
                  value={confirmEmail}
                  maxLength={45}
                  onChange={(e) => {
                    setConfirmEmail(e.target.value);
                  }}
                />
              </div>
              {userState.username !== confirmEmail && (
                <span style={{ color: "red" }}>
                  Both Email address must match.
                </span>
              )}
            </>
          )}
          <div className={classes.reset_password_wrapper}>
            <div>Password</div>

            <CustomButton
              variant="outline-primary"
              style={{ width: "max-content" }}
              onClick={() =>
                sendResetLinkMutation({ email: userData.username })
              }
            >
              Send Password Reset Link
            </CustomButton>
          </div>
          <MultiSelect
            label="Assign group"
            options={globalData.allGroups}
            selected={userState.groups}
            onChange={(e) => {
              {
                setUserState((prev) => {
                  return { ...prev, groups: e };
                });
              }
            }}
            placeholder="Please Select"
            className={classes.group_select}
          />
          <div className="_g-modal-button-wrapper_">
            <CustomButton variant="outline-secondary" onClick={handleClose}>
              Cancel
            </CustomButton>
            <Confirmation
              content={"Please confirm updating this user? "}
              disabled={isEmailInvalid || !isFormDirty()}
              Children={
                <CustomButton
                  variant="primary"
                  disabled={isEmailInvalid || !isFormDirty()}
                >
                  Save
                </CustomButton>
              }
              onConfirm={handleUpdateUser}
            />
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default EditUserModal;
